import { ReactNode } from 'react';
import { Box } from '@mui/material';

/**
 * 「OHANA AI にお任せください！」の各カードにある [before] → [after] コマの絵。
 *
 * 汎用アイコン1個では「アセスメント」と「加算書類の下書き」の違いが伝わらないため、
 * それぞれの中身（記入・表・音声・チェック・期限・防止）が一目で分かる線画にしている。
 * 画面キャプチャではなく図解なのは従来どおり。実物と異なるモックを置くと誤解を招くため。
 *
 * 実装はインラインSVG。ラスター画像にしないのは、
 *  - 6点あるので画像だと追加リクエストが6本増える（LCPに効く）
 *  - 配色をブランドカラーの定数だけで揃えられる
 *  - 拡大しても線が潰れない
 * ため。いずれも装飾なので aria-hidden。意味はタイル内のラベルとキャプションが担う。
 */

const TEAL = '#2aa1af'; // OHANA AI のブランドカラー
const PALE = '#e2f2f3'; // ティールの淡色（面の塗り）
const RED = '#e2574c'; // 期限など注意を促す箇所のみ

/** 6点で線幅・色・viewBoxを揃えるための共通ラッパ */
const Art = ({ children }: { children: ReactNode }) => (
    <Box component="svg" viewBox="0 0 120 90" role="presentation" aria-hidden="true" sx={{ width: '100%', height: 'auto', display: 'block' }}>
        <g fill="none" stroke={TEAL} strokeWidth={3.5} strokeLinecap="round" strokeLinejoin="round">
            {children}
        </g>
    </Box>
);

/** アセスメント：バインダーに書き込む */
export const ArtAssessment = () => (
    <Art>
        <rect x="26" y="14" width="52" height="66" rx="6" fill="#fff" />
        <rect x="42" y="8" width="20" height="11" rx="4" fill={PALE} />
        <path d="M36 34 h32 M36 46 h32 M36 58 h20" />
        <g transform="rotate(38 88 54)">
            <path d="M81 26 h14 v40 l-7 11 l-7 -11 z" fill={PALE} />
            <path d="M81 60 h14" />
        </g>
    </Art>
);

/** 個別支援計画書：表のある書類＋AIのきらめき */
export const ArtSupportPlan = () => (
    <Art>
        <rect x="22" y="10" width="58" height="72" rx="6" fill="#fff" />
        <rect x="32" y="20" width="26" height="7" rx="3.5" fill={TEAL} stroke="none" />
        <rect x="32" y="36" width="38" height="34" rx="2" />
        <path d="M32 47 h38 M32 58 h38 M51 36 v34" />
        <path d="M95 12 l4.5 11 l11 4.5 l-11 4.5 l-4.5 11 l-4.5 -11 l-11 -4.5 l11 -4.5 z" fill={PALE} />
        <path d="M89 46 l2.8 6.5 l6.5 2.8 l-6.5 2.8 l-2.8 6.5 l-2.8 -6.5 l-6.5 -2.8 l6.5 -2.8 z" fill={PALE} />
    </Art>
);

/** 加算書類の下書き：書類＋マイク（音声入力）＋AIのきらめき */
export const ArtDraft = () => (
    <Art>
        <rect x="16" y="12" width="50" height="66" rx="6" fill="#fff" />
        <path d="M26 30 h30 M26 42 h30 M26 54 h18" />
        <rect x="79" y="20" width="19" height="31" rx="9.5" fill={PALE} />
        <path d="M73 43 a15.5 15.5 0 0 0 31 0" />
        <path d="M88.5 58 v13 M79 71 h19" />
        <path d="M105 10 l2.6 6 l6 2.6 l-6 2.6 l-2.6 6 l-2.6 -6 l-6 -2.6 l6 -2.6 z" fill={PALE} />
    </Art>
);

/** 内容を確認：書類にチェック */
export const ArtCheck = () => (
    <Art>
        <rect x="18" y="10" width="54" height="68" rx="6" fill="#fff" />
        <path d="M28 28 h34 M28 40 h34 M28 52 h22" />
        <circle cx="84" cy="60" r="22" fill={PALE} />
        <path d="M73 60 l8 9 l16 -19" strokeWidth={5} />
    </Art>
);

/** 期限アラート：カレンダー＋ベル。期日の1日だけ赤で囲って注意を示す */
export const ArtAlert = () => (
    <Art>
        <rect x="12" y="20" width="62" height="56" rx="6" fill="#fff" />
        <path d="M12 36 h62" />
        <path d="M26 12 v14 M60 12 v14" strokeWidth={4} />
        <g fill={TEAL} stroke="none">
            <circle cx="26" cy="48" r="3.6" />
            <circle cx="43" cy="48" r="3.6" />
            <circle cx="26" cy="63" r="3.6" />
        </g>
        <circle cx="60" cy="48" r="7" stroke={RED} strokeWidth={3.2} />
        <path d="M96 24 c-9 0 -15 7 -15 15 c0 10 -3 13 -5 16 h40 c-2 -3 -5 -6 -5 -16 c0 -8 -6 -15 -15 -15 z" fill={PALE} />
        <path d="M96 18 v6" />
        <path d="M89 60 a7 7 0 0 0 14 0" />
    </Art>
);

/** 減算なし：盾にチェック */
export const ArtShield = () => (
    <Art>
        <path d="M60 8 L94 20 V46 C94 66 78 78 60 84 C42 78 26 66 26 46 V20 Z" fill={PALE} />
        <path d="M47 46 l10 11 l20 -22" strokeWidth={6} />
    </Art>
);
